/**
 * "Playlist" section of DashboardSettingsDialog (S13): auto-cycle this
 * dashboard with others in Present Mode (Esc to exit). This dashboard is
 * always shown first — the checklist below adds OTHERS to follow it.
 * Saved as `Dashboard.playlist` (S1 field); `undefined` disables cycling
 * and Present Mode just shows this one dashboard.
 */
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { Dashboard } from '../types/dashboardTypes';

const MIN_SECONDS = 5;
const DEFAULT_SECONDS = 30;

type Props = {
  dashboard: Dashboard;
  dashboards: Dashboard[];
  onSave: (playlist: Dashboard['playlist']) => void;
};

export function PlaylistSettingsSection({ dashboard, dashboards, onSave }: Props) {
  const { track } = useAnalytics();
  const playlist = dashboard.playlist;
  const enabled = playlist !== undefined;
  const others = dashboards.filter((d) => d.id !== dashboard.id);
  const selected = new Set((playlist?.dashboardIds ?? []).filter((id) => id !== dashboard.id));
  const seconds = playlist?.secondsPerDashboard ?? DEFAULT_SECONDS;

  const commit = (nextEnabled: boolean, nextSelected: Set<string>, nextSeconds: number) => {
    onSave(
      nextEnabled
        ? {
            dashboardIds: [dashboard.id, ...nextSelected],
            secondsPerDashboard: Math.max(MIN_SECONDS, nextSeconds),
          }
        : undefined,
    );
  };

  return (
    <div className="flex flex-col gap-2" data-testid="playlist-settings-section">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">Playlist</Label>
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => {
            // Toggle-both-ways (repo analytics rule) — fired here, not inside
            // `commit`, since that function is shared with the seconds input
            // and the per-dashboard checkboxes, which aren't toggles.
            track('feature:dashboard_playlist_toggle', { enabled: checked });
            commit(checked, selected, seconds);
          }}
          data-testid="playlist-enable-switch"
        />
      </div>
      {!enabled && (
        <p className="text-xs text-muted-foreground">
          Cycle this dashboard with others in Present Mode.
        </p>
      )}
      {enabled && (
        <div className="flex flex-col gap-2 rounded-md border border-border px-2.5 py-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="playlist-seconds" className="text-xs">
              Seconds per dashboard
            </Label>
            <Input
              id="playlist-seconds"
              type="number"
              min={MIN_SECONDS}
              value={seconds}
              onChange={(e) => commit(true, selected, Number(e.target.value) || MIN_SECONDS)}
              className="h-7 w-20 text-xs"
              data-testid="playlist-seconds-input"
            />
          </div>
          {others.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Create another dashboard to add it to this playlist.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                Also cycle through (this dashboard shows first):
              </span>
              {others.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={selected.has(d.id)}
                    data-testid={`playlist-pick-${d.id}`}
                    onCheckedChange={(checked) => {
                      const next = new Set(selected);
                      if (checked === true) next.add(d.id);
                      else next.delete(d.id);
                      commit(true, next, seconds);
                    }}
                  />
                  <span className="truncate">{d.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
